import { useEffect, useState, useCallback } from 'react';
import { adminApi } from '@/lib/adminApi';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Plus, Search, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight,
  MapPin, Star, StarOff, Phone, Globe, Eye, Sparkles, Store, Rocket, Fuel,
} from 'lucide-react';
import CampaignWizard from './wizard/CampaignWizard';
import EditCopyDialog from './sponsors/EditCopyDialog';
import SponsorAdPreview from './sponsors/SponsorAdPreview';
import { ContactRow } from '@/components/ContactRow';
import { ImageUploadInput } from '@/components/ImageUploadInput';
import { formatDominicaPhone } from '@/lib/formatPhone';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Listing {
  id: string;
  name: string;
  slug?: string | null;
  category: string | null;
  description: string | null;
  /** DB column: latitude */
  latitude: number | null;
  /** DB column: longitude */
  longitude: number | null;
  /** DB column: address_line */
  address_line: string | null;
  phone: string | null;
  /** DB column: website_url */
  website_url: string | null;
  /** DB column: logo_url */
  logo_url: string | null;
  /** DB column: is_featured */
  is_featured: boolean;
  /** DB column: is_public_utility — free always-on "Essentials" layer
   *  (ATMs, gas stations) shown on the map without any campaign. */
  is_public_utility?: boolean;
  /** DB column: tags — admin-curated search keywords (lowercased). */
  tags?: string[] | null;
  active?: boolean;
  // Campaign fields (if listing has a shop_local_pin campaign)
  campaign_id?: string | null;
  campaign_active?: boolean;
  headline?: string | null;
  body?: string | null;
  cta_text?: string | null;
  background_color?: string | null;
  creative_mode?: string | null;
  impressions?: number;
  clicks?: number;
  created_at: string;
}

interface ListingForm {
  name: string;
  category: string;
  description: string;
  /** Displayed as "Latitude" — maps to DB column latitude */
  lat: string;
  /** Displayed as "Longitude" — maps to DB column longitude */
  lng: string;
  address: string;
  phone: string;
  website: string;
  image_url: string;
  /** Comma-separated search keywords, e.g. "furniture, home, sofa". */
  tags: string;
  featured: boolean;
}

const defaultForm: ListingForm = {
  name: '', category: '', description: '', lat: '', lng: '',
  address: '', phone: '', website: '', image_url: '', tags: '', featured: false,
};

/** "furniture, Home , SOFA" → ["furniture","home","sofa"] (lowercased, deduped). */
function parseTags(raw: string): string[] {
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const t = part.trim().toLowerCase();
    if (t) seen.add(t);
  }
  return Array.from(seen);
}

export default function Listings() {
  const { isManager } = useAdminAuth();
  const [listings, setListings] = useState<Listing[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Listing | null>(null);
  const [form, setForm] = useState<ListingForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editCopyListing, setEditCopyListing] = useState<Listing | null>(null);
  const [previewListing, setPreviewListing] = useState<Listing | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Listing | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [forcePushListing, setForcePushListing] = useState<Listing | null>(null);
  const [forcePushSubmitting, setForcePushSubmitting] = useState(false);
  const limit = 15;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getListings({ page, limit, search: search || undefined });
      setListings(res.data || []);
      setTotal(res.total || 0);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(defaultForm); setDialogOpen(true); };

  const openEdit = (l: Listing) => {
    setEditing(l);
    setForm({
      name: l.name, category: l.category || '', description: l.description || '',
      lat: l.latitude?.toString() || '', lng: l.longitude?.toString() || '',
      address: l.address_line || '', phone: l.phone || '', website: l.website_url || '',
      image_url: l.logo_url || '', tags: (l.tags || []).join(', '), featured: l.is_featured,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name,
        category: form.category || null,
        description: form.description || null,
        latitude: form.lat ? parseFloat(form.lat) : null,
        longitude: form.lng ? parseFloat(form.lng) : null,
        address_line: form.address || null,
        phone: form.phone || null,
        website_url: form.website || null,
        logo_url: form.image_url || null,
        tags: parseTags(form.tags),
        is_featured: form.featured,
      };
      if (editing) {
        await adminApi.updateListing(editing.id, body);
        toast.success('Listing updated');
      } else {
        await adminApi.createListing(body);
        toast.success('Listing created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save listing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await adminApi.deleteListing(deleteConfirm.id);
      toast.success(`${deleteConfirm.name} deleted`);
      setDeleteConfirm(null);
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete listing');
    } finally {
      setDeleting(false);
    }
  };

  const toggleFeatured = async (l: Listing) => {
    const newFeatured = !l.is_featured;
    setListings(prev => prev.map(x => x.id === l.id ? { ...x, is_featured: newFeatured } : x));
    try {
      await adminApi.updateListing(l.id, { is_featured: newFeatured });
      toast.success(newFeatured ? `${l.name} featured` : `${l.name} unfeatured`);
    } catch (err: any) {
      setListings(prev => prev.map(x => x.id === l.id ? { ...x, is_featured: !newFeatured } : x));
      toast.error(err.message || 'Failed to update');
    }
  };

  /**
   * Toggle the free "Essentials" utility layer. Utility pins (ATMs, gas
   * stations) show on the map for everyone WITHOUT a campaign and are
   * never billed as ad impressions — the opposite of a featured/sponsored
   * pin. A row can be both (a sponsored gas station that's also a public
   * utility); the app dedupes and lets the sponsored styling win.
   */
  const toggleUtility = async (l: Listing) => {
    const next = !l.is_public_utility;
    setListings(prev => prev.map(x => x.id === l.id ? { ...x, is_public_utility: next } : x));
    try {
      await adminApi.updateListing(l.id, { is_public_utility: next });
      toast.success(next ? `${l.name} added to free Essentials layer` : `${l.name} removed from Essentials`);
    } catch (err: any) {
      setListings(prev => prev.map(x => x.id === l.id ? { ...x, is_public_utility: !next } : x));
      toast.error(err.message || 'Failed to update');
    }
  };

  const isListingPushable = (l: Listing): boolean => {
    if (!l.campaign_id || !l.campaign_active) return false;
    return true;
  };

  const getListingPushDisabledReason = (l: Listing): string | null => {
    if (!l.campaign_id) return 'No campaign attached. Promote this listing first';
    if (!l.campaign_active) return 'Campaign is inactive';
    return null;
  };

  const handleForcePush = async () => {
    if (!forcePushListing || !forcePushListing.campaign_id) return;
    setForcePushSubmitting(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_force_push_campaign', {
        p_campaign_id: forcePushListing.campaign_id,
      });
      if (rpcErr) throw rpcErr;
      const result = data as any;
      if (result?.ok === false) {
        if (result.error === 'forbidden') {
          toast.error("You don't have permission. Ask a platform admin.");
        } else if (result.error === 'campaign_not_found') {
          toast.error('Campaign not found. Refreshing the list.');
        } else {
          toast.error(result.error || 'Force push failed');
        }
      } else if (result?.will_serve === false) {
        toast.warning(`Pushed, but campaign won't serve until you fix this: ${result.warning}`, { duration: 8000 });
      } else {
        toast.success(`Pushed ${forcePushListing.name} and cleared ${result?.dismissals_wiped ?? 0} dismissals.`);
      }
      setForcePushListing(null);
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Force push failed');
    } finally {
      setForcePushSubmitting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Store className="w-6 h-6 text-teal-400" /> Listings
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Local businesses with featured map pins: restaurants, hotels, tours and shops
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openCreate} variant="outline" className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500">
            <Plus className="w-4 h-4 mr-2" /> Add Listing
          </Button>
          <Button onClick={() => setWizardOpen(true)} className="bg-teal-600 hover:bg-teal-500 text-white">
            <Sparkles className="w-4 h-4 mr-2" /> Promote Listing
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          placeholder="Search by name or category..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="pl-9 bg-gray-900 border-gray-800 text-white placeholder:text-gray-500"
        />
      </div>

      {/* Table */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 bg-gray-800/50 rounded" />
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div className="p-12 text-center">
              <Store className="w-10 h-10 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500">No listings found</p>
              <p className="text-xs text-gray-600 mt-1">Click "Add Listing" to create a local business entry.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <TableHead className="text-gray-400">Business</TableHead>
                  <TableHead className="text-gray-400">Category</TableHead>
                  <TableHead className="text-gray-400">Location</TableHead>
                  <TableHead className="text-gray-400">Contact</TableHead>
                  <TableHead className="text-gray-400 text-center">Featured</TableHead>
                  <TableHead className="text-gray-400 text-center">Imps</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((l) => (
                  <TableRow key={l.id} className="border-gray-800 hover:bg-gray-800/30">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {l.logo_url ? (
                          <img src={l.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover border border-gray-700" />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                            <MapPin className="w-4 h-4 text-teal-400" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="text-white font-medium text-sm">{l.name}</p>
                            {l.is_public_utility && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 border-sky-500/40 text-sky-300">
                                <Fuel className="w-2.5 h-2.5 mr-0.5" /> Free
                              </Badge>
                            )}
                          </div>
                          {l.address_line && (
                            <p className="text-[11px] text-gray-500 truncate max-w-[180px]">{l.address_line}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs border-gray-700 text-gray-300">
                        {l.category || 'Uncategorized'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm">
                      {l.latitude && l.longitude ? (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {l.latitude.toFixed(4)}, {l.longitude.toFixed(4)}
                        </span>
                      ) : (
                        <span className="text-gray-600">No GPS</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        {l.phone && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {formatDominicaPhone(l.phone)}
                          </span>
                        )}
                        {l.website_url && (
                          <a href={l.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-400 hover:underline flex items-center gap-1 truncate max-w-[140px]">
                            <Globe className="w-3 h-3" /> {l.website_url.replace(/^https?:\/\//, '')}
                          </a>
                        )}
                        {!l.phone && !l.website_url && <span className="text-gray-600 text-xs">–</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`w-8 h-8 ${l.is_featured ? 'text-amber-400 hover:text-amber-300' : 'text-gray-600 hover:text-amber-400'}`}
                              onClick={() => toggleFeatured(l)}
                            >
                              {l.is_featured ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{l.is_featured ? 'Remove featured (sponsored)' : 'Mark as featured (sponsored)'}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`w-8 h-8 ${l.is_public_utility ? 'text-sky-400 hover:text-sky-300' : 'text-gray-600 hover:text-sky-400'}`}
                              onClick={() => toggleUtility(l)}
                            >
                              <Fuel className={`w-4 h-4 ${l.is_public_utility ? 'fill-current' : ''}`} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {l.is_public_utility
                              ? 'Remove from free Essentials layer'
                              : 'Add to free Essentials layer (ATM or gas, shown free, no campaign)'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-gray-400 text-sm tabular-nums">
                      {(l.impressions || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        {l.campaign_id && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-teal-400 w-8 h-8" onClick={() => setPreviewListing(l)}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Preview ad</TooltipContent>
                          </Tooltip>
                        )}
                        {l.campaign_id && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-emerald-400 w-8 h-8" onClick={() => setEditCopyListing(l)}>
                                <Sparkles className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit copy</TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`w-8 h-8 ${isListingPushable(l) ? 'text-amber-400 hover:text-amber-300' : 'text-gray-600 cursor-not-allowed'}`}
                              disabled={!isListingPushable(l)}
                              onClick={() => setForcePushListing(l)}
                            >
                              <Rocket className="w-3.5 h-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
                            {getListingPushDisabledReason(l) || 'Force Push'}
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white w-8 h-8" onClick={() => openEdit(l)}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit listing</TooltipContent>
                        </Tooltip>
                        {isManager && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-gray-400 hover:text-red-400 w-8 h-8"
                                onClick={() => setDeleteConfirm(l)}
                                aria-label={`Delete ${l.name}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{total} listings total</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-gray-400">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="text-gray-400">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Listing Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Listing' : 'New Listing'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Business Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="e.g. Ma Pawol Restaurant" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="e.g. Restaurant, Hotel, Tour" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-gray-800 border-gray-700 text-white min-h-[80px]" placeholder="Brief description..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-300">Latitude</Label>
                <Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="15.4150" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Longitude</Label>
                <Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="-61.3710" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="e.g. 12 Cork Street, Roseau" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-300">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="+1 767 ..." />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Website</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="https://..." />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Logo / image (upload or paste a URL)</Label>
              <ImageUploadInput value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} folder="listings" />
              {form.image_url && (
                <img src={form.image_url} alt="" className="w-full h-32 object-cover rounded-lg border border-gray-700 mt-2" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Search tags</Label>
              <Input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="e.g. furniture, home, sofa, mattress"
              />
              <p className="text-[11px] text-gray-500">
                Comma-separated keywords riders might type in "Find a place". Helps
                the app match specific intents the category can't (e.g. "furniture"
                vs "clothing" when both are a Store).
              </p>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
              <Label className="text-gray-300">Featured (shows as highlighted pin on map)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || !form.name} className="bg-teal-600 hover:bg-teal-500 text-white">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote Listing Wizard — listing mode */}
      <CampaignWizard
        mode="listing"
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onPublished={() => fetchData()}
      />

      {/* Edit Copy Dialog */}
      {editCopyListing?.campaign_id && (
        <EditCopyDialog
          campaign={{
            id: editCopyListing.campaign_id,
            brand_name: editCopyListing.name,
            placement: 'map_pin_featured',
            image_url: editCopyListing.logo_url,
            cta_url: editCopyListing.website_url,
          }}
          open={!!editCopyListing}
          onOpenChange={(o) => { if (!o) setEditCopyListing(null); }}
          onUpdated={() => fetchData()}
        />
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewListing} onOpenChange={(o) => { if (!o) setPreviewListing(null); }}>
        <DialogContent className="bg-gray-950 border-gray-800 text-white max-w-sm p-6">
          <DialogHeader>
            <DialogTitle className="text-sm text-gray-400">Preview: {previewListing?.name}</DialogTitle>
          </DialogHeader>
          {previewListing && (
            <div className="flex justify-center pt-2">
              <SponsorAdPreview
                data={{
                  headline: previewListing.headline || previewListing.name,
                  body: previewListing.body || previewListing.description || '',
                  ctaText: previewListing.cta_text || 'Visit',
                  backgroundColor: previewListing.background_color || '#0d4f4f',
                  logoUrl: previewListing.logo_url || '',
                  placement: 'map_pin_featured',
                  creativeMode: (previewListing.creative_mode as any) || 'ai_generated',
                  brandName: previewListing.name,
                  templateKey: 'shop_local_pin',
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Force Push Single Campaign Modal */}
      <Dialog open={!!forcePushListing} onOpenChange={(o) => { if (!o) setForcePushListing(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Force-push <span className="text-emerald-400">{forcePushListing?.name}</span>?</DialogTitle>
            <DialogDescription className="text-gray-400">
              This wipes every passenger / driver's dismissal of this listing's campaign and tells the live app to refresh on every device within seconds.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-gray-800/50 rounded-lg p-3 text-sm text-gray-300 space-y-1">
              <p className="font-medium text-gray-200">Will reach users who:</p>
              <ul className="list-disc list-inside text-gray-400 text-xs space-y-0.5">
                <li>Have not yet hit today's frequency cap</li>
                <li>Are inside the campaign's audience / corridor / hour / weather rules</li>
                <li>Are running the app currently or open it next</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setForcePushListing(null)} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleForcePush}
              disabled={forcePushSubmitting}
              className="bg-amber-600 hover:bg-amber-500 text-white"
            >
              {forcePushSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Rocket className="w-4 h-4 mr-2" />
              Force Push Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete listing?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-400 py-2">
            This will permanently remove <span className="text-white font-medium">{deleteConfirm?.name}</span> from the map. Any associated campaign will also be removed.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="text-gray-400">Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-500 text-white">
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
